import { t } from 'i18next';

import { WebsocketMessage } from '../../../../typings/websocket-messages';
import { TokenService } from '../../services/authentication/token.service';
import { publish, SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { assertEnvVariable } from '../environment';
import { isDefined } from '../variables';

function parseWebsocketMessage(data: any): WebsocketMessage {
  const websocketMessage = JSON.parse(data as string);

  // Simple parsing of the message, add more parsing logic later.
  if (!isDefined(websocketMessage.message)) {
    throw new Error(`Websocket message did not contain any message: ${JSON.stringify(data)}`);
  }

  return websocketMessage;
}

export class WebsocketManager {
  private _status: 'connecting' | 'disconnected' | 'connected' = 'disconnected';
  private websocket?: WebSocket;
  private pingTimeoutId?: ReturnType<typeof setTimeout>;
  private pongTimeoutId?: ReturnType<typeof setTimeout>;
  private reconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private _intentionalDisconnect = false;
  private reconnectAttempt = 0;

  // AWS Gateway is supposed to close connection after being idle for 10 minutes.
  // However the connection closes after 1 minute. Cause is unknown.
  private pingInterval = 30 * 1000;
  private pongTimeoutMs = 10 * 1000;
  private maxReconnectDelayMs = 30_000;

  private static _instance: WebsocketManager;

  get status() {
    return this._status;
  }

  get connected() {
    return this._status === 'connected';
  }

  static get instance() {
    return (this._instance ??= new WebsocketManager());
  }

  private constructor() {
    // Force-close the socket when the browser detects we've gone offline so the
    // bell turns red immediately instead of waiting for the next ping cycle.
    window.addEventListener('offline', () => this.websocket?.close());
    window.addEventListener('online', () => this.maybeReconnect());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.maybeReconnect();
      }
    });
  }

  disconnect() {
    this._intentionalDisconnect = true;
    this.reconnectAttempt = 0;
    this.websocket?.close();
    this.reset();
  }

  private reset() {
    this._status = 'disconnected';
    publish(SubscriptionTopic.WebsocketStatusChanged, { status: 'disconnected' });
    this.websocket = undefined;
    clearTimeout(this.pingTimeoutId);
    clearTimeout(this.pongTimeoutId);
    clearTimeout(this.reconnectTimeoutId);
    this.reconnectTimeoutId = undefined;
  }

  // Reconnects immediately, cancelling any pending scheduled reconnect.
  // No-ops if intentionally disconnected, not authenticated, or already connecting/connected.
  private maybeReconnect() {
    if (this._intentionalDisconnect) {
      return;
    }

    const token = new TokenService().get();
    if (!isDefined(token)) {
      return;
    }

    // Accelerate any pending scheduled reconnect
    if (isDefined(this.reconnectTimeoutId)) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = undefined;
    }

    if (this._status !== 'disconnected') {
      return;
    }

    this.connect();
  }

  /**
   * Waits until the websocket connection is established.
   * If the user is disconnected it will automatically attempt
   * to reconnect.
   */
  async waitForWebsocketConnection() {
    if (this.connected) {
      return;
    }

    // Only connect if it's not already connecting.
    if (this.status === 'disconnected') {
      this.connect();
    }

    publish(SubscriptionTopic.ToastrMessage, {
      type: 'info',
      message: t('reconnecting', { ns: 'common' }),
    });

    const checkIntervalMs = 250;
    const awsApiGatewayTimeoutMs = 30000;
    const connectStarted = Date.now();
    return new Promise((resolve, reject) => {
      const setCheckConnectionTimeout = () => {
        setTimeout(() => {
          if (this.connected) {
            resolve(undefined);
          } else {
            const timeElapsedMs = Date.now() - connectStarted;
            if (timeElapsedMs < awsApiGatewayTimeoutMs) {
              setCheckConnectionTimeout();
            } else {
              publish(SubscriptionTopic.ToastrMessage, {
                type: 'error',
                message: t('connectionFailed', { ns: 'common' }),
              });
              reject(new Error('Connection timed out.'));
            }
          }
        }, checkIntervalMs);
      };

      setCheckConnectionTimeout();
    });
  }

  connect() {
    if (this._status !== 'disconnected') {
      return;
    }

    this._intentionalDisconnect = false;

    const websocketUrl = assertEnvVariable('WEBSOCKET_URL');
    const websocketEndpoint = `${websocketUrl}?token=${new TokenService().get()}`;

    this._status = 'connecting';
    publish(SubscriptionTopic.WebsocketStatusChanged, { status: 'connecting' });
    this.websocket = new WebSocket(websocketEndpoint);

    this.websocket.onopen = () => {
      this._status = 'connected';
      if (this.reconnectAttempt > 0) {
        publish(SubscriptionTopic.OnWebsocketReconnected);
      }
      this.reconnectAttempt = 0;
      publish(SubscriptionTopic.WebsocketStatusChanged, { status: 'connected' });
      this.schedulePing();
    };

    this.websocket.onclose = () => {
      this.reset();

      if (this._intentionalDisconnect) {
        return;
      }

      const token = new TokenService().get();
      if (!isDefined(token)) {
        return;
      }

      this.reconnectAttempt++;
      const delay = this.nextReconnectDelayMs();
      console.error(
        `WebSocket closed. Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempt})...`,
      );
      this.reconnectTimeoutId = setTimeout(() => {
        const currentToken = new TokenService().get();
        if (isDefined(currentToken) && !this._intentionalDisconnect) {
          this.connect();
        }
      }, delay);
    };

    this.websocket.onmessage = (event) => this.handleMessage(event);

    // Only log on error — the onclose event fires immediately after and handles
    // reconnection with backoff. Showing a toaster here would spam on every retry.
    this.websocket.onerror = () => {
      console.error('WebSocket connection error.');
    };
  }

  // Exponential backoff capped at maxReconnectDelayMs, with ±1s jitter to avoid stampedes.
  private nextReconnectDelayMs(): number {
    const base = Math.min(this.maxReconnectDelayMs, 1000 * 2 ** this.reconnectAttempt);
    return base + Math.random() * 1000;
  }

  private schedulePing() {
    this.pingTimeoutId = setTimeout(() => this.ping(), this.pingInterval);
  }

  handleMessage(event: MessageEvent) {
    try {
      const data = parseWebsocketMessage(event.data);

      // Ignore pong messages.
      if (data.message === 'Pong') {
        clearTimeout(this.pongTimeoutId);
        this.schedulePing();
        return;
      }

      // Publish the event so we can subscribe to it in other components.
      publish(SubscriptionTopic.WebsocketMessageRecieved, data);
      handleBatchDownloadCompletedMessage(data);
    } catch (err) {
      console.error(JSON.stringify(err));
    }
  }

  ping() {
    if (!this.connected || !isDefined(this.websocket)) {
      this.maybeReconnect();
      return;
    }

    try {
      this.websocket.send(JSON.stringify({ action: 'ping' }));
      // Catches silent failures (e.g. connected to a router with no internet, or a
      // server-side drop) where the 'offline' event never fires.
      this.pongTimeoutId = setTimeout(() => {
        console.error('Pong timeout, triggering reconnect.');
        this.reset();
        this.maybeReconnect();
      }, this.pongTimeoutMs);
    } catch {
      console.error('Ping failed, triggering reconnect.');
      this.reset();
      this.maybeReconnect();
    }
  }

  /**
   * Sends a message to the websocket server.
   * @param message - The message payload to send (will be JSON stringified)
   * @throws Error if not connected to websocket
   */
  sendToUserId(message: WebsocketMessage, userId: string) {
    if (!this.connected || !isDefined(this.websocket)) {
      throw new Error('Error sending message. Not connected to websocket.');
    }

    this.websocket.send(
      JSON.stringify({
        action: 'send',
        message,
        targetUserId: userId,
      }),
    );
  }
}

function handleBatchDownloadCompletedMessage(data: WebsocketMessage) {
  if (data.message === 'BatchDownloadCompleted' && document.visibilityState === 'visible') {
    window.location.assign(data.downloadUrl);
  }
}
